<template>
  <VueDatePicker
    v-model="internalDate"
    :enable-time-picker="false"
    :dark="isDark"
    :min-date="parsedMin"
    :max-date="parsedMax"
    :disabled="disabled"
    :readonly="readonly"
    :placeholder="placeholder"
    :formats="{ input: displayFormat }"
    :locale="dateFnsLocale"
    auto-apply
    text-input
    :teleport="false"
    class="b-form-datepicker-compat"
    :class="[size ? `dp-size-${size}` : '']"
  />
</template>

<script>
import { VueDatePicker } from '@vuepic/vue-datepicker'
import { es } from 'date-fns/locale'

export default {
  name: 'BFormDatepicker',
  components: { VueDatePicker },
  inheritAttrs: false,
  props: {
    modelValue: { type: [String, Date], default: undefined },
    value: { type: [String, Date], default: undefined },
    placeholder: { type: String, default: '' },
    buttonOnly: { type: Boolean, default: false },
    buttonVariant: { type: String, default: 'secondary' },
    size: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    min: { type: [String, Date], default: undefined },
    max: { type: [String, Date], default: undefined },
    dateFormatOptions: { type: Object, default: undefined },
    locale: { type: String, default: undefined },
    showDecadeNav: { type: Boolean, default: false },
    name: { type: String, default: undefined },
    required: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'input', 'change'],
  data() {
    return {
      isDark: document.body.classList.contains('dark-layout'),
      observer: null,
      dateFnsLocale: es,
    }
  },
  computed: {
    currentValue() {
      return this.modelValue !== undefined ? this.modelValue : this.value
    },
    internalDate: {
      get() {
        if (!this.currentValue) return null
        const str = this.toDateString(this.currentValue)
        if (!str) return null
        const [y, m, d] = str.split('-').map(Number)
        return new Date(y, m - 1, d)
      },
      set(v) {
        if (!v) {
          this.$emit('update:modelValue', '')
          this.$emit('input', '')
          this.$emit('change', '')
          return
        }
        const d = v instanceof Date ? v : new Date(v)
        const str = [
          d.getFullYear(),
          String(d.getMonth() + 1).padStart(2, '0'),
          String(d.getDate()).padStart(2, '0'),
        ].join('-')
        this.$emit('update:modelValue', str)
        this.$emit('input', str)
        this.$emit('change', str)
      },
    },
    parsedMin() {
      return this.parseDate(this.min)
    },
    parsedMax() {
      return this.parseDate(this.max)
    },
    displayFormat() {
      const opts = this.dateFormatOptions
      if (!opts) return 'dd/MM/yyyy'
      const dayPart = 'dd'
      const monthPart = opts.month === 'short' ? 'MMM' : 'MM'
      const yearPart = opts.year === '2-digit' ? 'yy' : 'yyyy'
      return `${dayPart}/${monthPart}/${yearPart}`
    },
  },
  mounted() {
    this.observer = new MutationObserver(() => {
      this.isDark = document.body.classList.contains('dark-layout')
    })
    this.observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect()
  },
  methods: {
    toDateString(v) {
      if (!v) return ''
      if (v instanceof Date) {
        const off = v.getTimezoneOffset() * 60000
        return new Date(v.getTime() - off).toISOString().slice(0, 10)
      }
      if (typeof v === 'string') return v.slice(0, 10)
      return ''
    },
    parseDate(v) {
      if (!v) return undefined
      const str = this.toDateString(v)
      if (!str) return undefined
      const [y, m, d] = str.split('-').map(Number)
      return new Date(y, m - 1, d)
    },
  },
}
</script>

<style>
.b-form-datepicker-compat {
  --dp-font-size: 1rem;
  --dp-border-radius: 0.357rem;
  --dp-input-padding: 0.438rem 2.5rem 0.438rem 2.2rem;
  --dp-input-icon-padding: 2.2rem;
  --dp-font-family: inherit;
}

.b-form-datepicker-compat .dp--input {
  min-height: 2.714rem;
}

.b-form-datepicker-compat .dp--clear-btn {
  inset-inline-end: 8px;
}

.dark-layout .b-form-datepicker-compat {
  --dp-background-color: #283046;
  --dp-text-color: #b4b7bd;
  --dp-border-color: #3b4253;
  --dp-menu-border-color: #3b4253;
  --dp-border-color-hover: #3b4253;
  --dp-border-color-focus: #7367f0;
  --dp-hover-color: #242b3d;
  --dp-hover-text-color: #d0d2d6;
  --dp-hover-icon-color: #d0d2d6;
  --dp-primary-color: #7367f0;
  --dp-primary-text-color: #fff;
  --dp-secondary-color: #676d7d;
  --dp-icon-color: #b4b7bd;
  --dp-disabled-color: #161d31;
  --dp-disabled-color-text: #676d7d;
  --dp-scroll-bar-background: #283046;
  --dp-scroll-bar-color: #3b4253;
  --dp-highlight-color: rgba(115, 103, 240, 0.15);
}

.dp-size-sm .dp--input {
  min-height: 2.142rem;
  font-size: 0.75rem;
}

.dp-size-lg .dp--input {
  min-height: 3rem;
  font-size: 1rem;
}
</style>
